// src/components/admin/contract/ContractList.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  fetchFilteredContracts,
  deleteContract,
  Contract,
  ContractFilterRequest,
} from './ContractApi';
import { CONTRACT_STATUSES } from '../../constants/contractConstants';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';



const formatDate = (date?: string) => (date ? new Date(date).toLocaleDateString() : '-');

const ContractList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalPages: 1,
    totalElements: 0,
  });
  const [sortConfig, setSortConfig] = useState({
    field: 'createdAt',
    direction: 'desc',
  });

  const [filters, setFilters] = useState<ContractFilterRequest>({
    bidId: '',
    contractName: '',
    referenceNo: '',
    status: [],
    licenseHolderName: '',
    contractType: '',
    dateRange: { from: '', to: '' },
    pagination: { page: 0, size: 10 },
    sort: { field: 'createdAt', direction: 'desc' },
  });

  useEffect(() => {
    if (location.state?.filters) {
      setFilters((prev) => ({
        ...prev,
        ...location.state.filters,
        pagination: { ...prev.pagination, page: 0 },
      }));
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const loadContracts = async () => {
    setLoading(true);
    try {
      const response = await fetchFilteredContracts(filters);
      const data = response.data;
      setContracts(data.content);
      setPagination({
        page: data.number,
        size: data.size,
        totalPages: data.totalPages,
        totalElements: data.totalElements,
      });
    } catch (err) {
      setError('Failed to fetch contracts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContracts();
  }, [filters]);

  const updateFilter = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      pagination: { ...prev.pagination, page: 0 },
    }));
  };

  const updateDateFilter = (key: 'from' | 'to', value: string) => {
    setFilters((prev) => ({
      ...prev,
      dateRange: { ...prev.dateRange, [key]: value },
      pagination: { ...prev.pagination, page: 0 },
    }));
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const size = parseInt(e.target.value, 10) || 1;
    setFilters((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, size, page: 0 },
    }));
  };

  const changePage = (newPage: number) => {
    setFilters((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, page: newPage },
    }));
  };

  const handleSort = (field: string) => {
    const direction =
      sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ field, direction });
    setFilters((prev) => ({ ...prev, sort: { field, direction } }));
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this contract?')) {
      await deleteContract(id);
      loadContracts();
    }
  };


  const handleReset = () => {
    setFilters({
      bidId: '',
      contractName: '',
      referenceNo: '',
      licenseHolderName: '', // ✅ reset too
      contractType: '',
      status: [],
      dateRange: { from: '', to: '' },
      pagination: { page: 0, size: 10 },
      sort: { field: 'createdAt', direction: 'desc' },
    });
  };


  return (
    <div className="p-6">
   <div className="mb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-sm text-gray-700 hover:text-gray-900"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back
        </button>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Contracts</h2>
        <button
          onClick={() => navigate('/contracts/create')}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Create Contract
        </button>
      </div>

      {/* Filters Section */}
      <div className="bg-gray-100 p-4 rounded mb-2">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          {['bidId', 'contractName', 'referenceNo','licenseHolderName','contractType'].map((field) => (
            <div key={field}>
              <label className="text-sm font-medium mb-1 block">
                {field.charAt(0).toUpperCase() + field.slice(1)}
              </label>
              <input
                type="text"
                name={field}
                placeholder={`Enter ${field}`}
                value={(filters as any)[field]}
                onChange={updateFilter}
                className="border px-3 py-2 rounded w-full"
              />
            </div>
          ))}

          <div>
            <label className="text-sm font-medium mb-1 block">Status</label>
            <select
              name="status"
              multiple
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  status: Array.from(e.target.selectedOptions, (opt) => opt.value),
                  pagination: { ...prev.pagination, page: 0 },
                }))
              }
              className="border px-3 py-2 rounded w-full  "
            >
              {CONTRACT_STATUSES.map((s) => (
                <option key={s} value={s} title={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">From Date</label>
            <input
              type="date"
              value={filters.dateRange?.from?.split('T')[0] || ''}
              onChange={(e) => updateDateFilter('from', `${e.target.value}T00:00:00Z`)}
              className="border px-3 py-2 rounded w-full"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">To Date</label>
            <input
              type="date"
              value={filters.dateRange?.to?.split('T')[0] || ''}
              onChange={(e) => updateDateFilter('to', `${e.target.value}T23:59:59Z`)}
              className="border px-3 py-2 rounded w-full"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Page Size</label>
            <input
              type="number"
              min={1}
              value={filters.pagination.size}
              onChange={handlePageSizeChange}
              className="border px-2 py-1 rounded w-full max-w-[100px]"
            />
          </div>
        </div>

        
          <div className="md:col-span-3 flex items-end gap-2">
            <button
              onClick={loadContracts}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Apply Filters
            </button>
            <button
              onClick={handleReset}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Reset
            </button>
            <button
              onClick={() => navigate('/contracts')}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Back
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto border border-gray-300 text-sm">
            <thead className="bg-gray-200">
              <tr>
                {[
                  'bidId',
                  'contractName',
                  'contractType',
                  'status',
                  'paperCost',
                  'tenderBidValue',
                  'tenderActualValue',
                  'actualStartDate',
                  'actualEndDate'
                 
                ].map((field) => (
                  <th
                    key={field}
                    onClick={() => handleSort(field)}
                    className="border px-4 py-2 text-left font-semibold cursor-pointer hover:bg-gray-300"
                  >
                    {field.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}{' '}
                    {sortConfig.field === field
                      ? sortConfig.direction === 'asc'
                        ? '▲'
                        : '▼'
                      : ''}
                  </th>
                ))}
                <th className="border px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((contract) => (
                <tr key={contract.id} className="hover:bg-gray-50">
                  <td className="border px-4 py-2">{contract.bidId}</td>
                  <td className="border px-4 py-2">{contract.contractName}</td>
                  <td className="border px-4 py-2">{contract.contractType}</td>
                  <td className="border px-4 py-2">{contract.status}</td>
                  <td className="border px-4 py-2">{contract.paperCost}</td>
                  <td className="border px-4 py-2">{contract.tenderBidValue}</td>
                  <td className="border px-4 py-2">{contract.tenderActualValue}</td>
                  <td className="border px-4 py-2">{formatDate(contract.actualStartDate)}</td>
                  <td className="border px-4 py-2">{formatDate(contract.actualEndDate)}</td>
               
                  <td className="border px-4 py-2 flex flex-wrap gap-2">
                    <button
                      className="bg-blue-500 text-white px-2 py-1 rounded"
                      onClick={() => navigate(`/contracts/${contract.id}`)}
                    >
                      View
                    </button>
                    <button
                      className="bg-yellow-500 text-white px-2 py-1 rounded"
                      onClick={() => navigate(`/contracts/${contract.id}/edit`)}
                    >
                      Edit
                    </button>
                    <button
                      className="bg-red-600 text-white px-2 py-1 rounded"
                      onClick={() => handleDelete(contract.id)}
                    >
                      Delete
                    </button>
                    <button
                      className="bg-indigo-500 text-white px-2 py-1 rounded"
                      onClick={() => navigate(`/contracts/${contract.id}/security-deposit`)}
                    >
                      Add Security Deposit
                    </button>
                    <button
                      className="bg-teal-600 text-white px-2 py-1 rounded"
                      onClick={() => navigate(`/contracts/${contract.id}/supporting-docs`)}
                    >
                      Add Supporting Docs
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-between mt-4 items-center">
            <span>
              Showing page {pagination.page + 1} of {pagination.totalPages} —{' '}
              {pagination.totalElements} total records
            </span>
            <div className="flex gap-2">
              <button
                disabled={pagination.page === 0}
                onClick={() => changePage(pagination.page - 1)}
                className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
              >
                Prev
              </button>
              <button
                disabled={pagination.page >= pagination.totalPages - 1}
                onClick={() => changePage(pagination.page + 1)}
                className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractList;